import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrandingStore } from '../stores/brandingStore';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const branding = useBrandingStore((s) => s.branding);

  // Redirect authenticated users to /projects (New Home)
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/projects', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Original images array
  const originalImages = [
    '/home/photo1.jpeg',
    '/home/photo2.jpeg',
    '/home/photo3.jpeg',
    '/home/photo4.jpeg',
    '/home/photo5.jpeg'
  ];

  // Carousel state with shuffled images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselImages] = useState(() => shuffleArray(originalImages));

  // Auto-rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % carouselImages.length
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // Use full screen height if logged out (always true here essentially)
  // Use vh (viewport height) instead of dvh to prevent layout shifts when mobile URL bar appears/disappears
  const heroHeight = 'h-[100vh] md:h-[100vh]';
  const unauthBannerText = branding?.bannerText || 'Hello World';

  // If authenticated, we render nothing while redirecting
  if (isAuthenticated) {
    return null; 
  }

  return (
    <div>
      {/* Hero Section */}
      <div className={`${heroHeight} relative overflow-hidden`} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Carousel Background Images */}
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${image}')`,
              transform: 'translate3d(0,0,0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
            }}
          />
        ))}
        
        {/* Subtle Black/White Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/40 pointer-events-none"></div>

        {/* Header for logged out users */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 sm:p-6" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
          {/* Logo */}
          <div className="text-center">
            <img 
              src="/logo_white.webp" 
              alt="OKIO" 
              className="h-8 sm:h-10 w-auto mx-auto"
            />
          </div>
          
          {/* Buttons */}
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-white/10 dark:bg-white/20 backdrop-blur-sm border border-white/20 dark:border-white/30 text-white font-medium rounded-lg hover:bg-white/20 dark:hover:bg-white/30 transition-colors text-sm sm:text-base min-h-[44px]"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-6 text-center pointer-events-none">
          {/* Greeting */}
          <div className="mb-8 sm:mb-12 max-w-4xl">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              {unauthBannerText}
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
